import {pwywCurrencyRowSchema} from "@/features/pricing";
import yup from "../../../../../utils/yup-extended";
import {IS_PWYW_ENABLED} from "../../../../../services/config";

/**
 * The pricing rules shared by the two offering types that keep their base currency in
 * top-level scalars — live events and booking services.
 *
 * Both spell the Pricing Model `callPriceType`, and both had these same four rules
 * written out twice before Pay What You Want needed changing in both places at once.
 */

/**
 * A currency row under Free or Single Fee — unchanged from before Pay What You Want
 * existed, deliberately: shipping the third model must not move the other two.
 */
const singleFeeCurrencyRowSchema = yup.object().shape({
  callPriceCurrency: yup.string().trim().required("Currency is required"),
  callPrice: yup
    .number()
    .required("Price is required")
    .min(1, "Can't be less than 1")
    .default(1)
    .typeError("Must be a number"),
});

/** With the flag off, Pay What You Want is not a Pricing Model a coach can submit. */
const selectablePriceTypes = IS_PWYW_ENABLED
  ? ["paid", "free", "pwyw"]
  : ["paid", "free"];

/**
 * The Pricing Model, the base currency's price and the currency rows.
 *
 * Under Pay What You Want the base currency moves into `currencies[0]` alongside the
 * others, so the scalar price is not a field the coach is filling in and is not required
 * of them — the mapper reads it back off row one on the way out.
 */
export const baseScalarPricingValidationSchemaFields = {
  callPriceType: yup.string().required().oneOf(selectablePriceTypes, "Select a cost"),
  callPriceCurrency: yup.string().required("Currency is required").trim(),
  callPrice: yup
    .number()
    // Live events have always defaulted this to 1. Inert under the other two models,
    // where nothing reads the scalar price, and required under Single Fee anyway.
    .default(1)
    .typeError("Must be a number")
    .when("callPriceType", {
      is: (callPriceType: string) => callPriceType === "paid",
      then: (schema) =>
        schema.required("Price is required").min(1, "Can't be less than 1"),
      otherwise: (schema) => schema,
    }),
  currencies: yup.array().when("callPriceType", (callPriceType: string, schema) => {
    if (IS_PWYW_ENABLED && callPriceType === "pwyw") {
      return schema.of(pwywCurrencyRowSchema);
    }

    // Free renders no currency rows at all, so holding whatever the array happens to
    // carry to a Single Fee price would block the coach on an error they can neither see
    // nor clear. Digital products skip the rows under Free for the same reason.
    if (callPriceType === "free") {
      return schema;
    }

    return schema.of(singleFeeCurrencyRowSchema);
  }),
};
